// ============================================================
// WorkspaceNotificationRail — the workspace intelligence rail.
//
// Not toast spam: a persistent, channel-grouped signal surface
// (NotificationBar reference) that shows what needs attention —
// activation status, approvals, agent events, receipts — without
// opening every tab.
//
// Data honesty:
//   • items come from the caller (real sources only);
//   • `sourceLabel` names where the signal comes from;
//   • an empty rail renders an HONEST empty state — it never
//     invents notifications and never hides blockers.
//
//   items:    [{ id, channel, accent?, glyph?, title, body?, time,
//                isNew?, urgent?, onOpen? }]
//   channels: [{ key, label, accent, glyph? }] — filter chips +
//             per-channel accents; optional.
// ============================================================

import { useMemo, useState } from 'react'
import WorkspaceNotificationItem from './WorkspaceNotificationItem'
import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export default function WorkspaceNotificationRail({
  title = 'SIGNAL',
  items = [],
  channels = [],
  loading = false,
  live = false,          // pulsing live dot in the header
  clock,                 // optional preformatted time string
  filterable = true,
  maxItems = 30,
  compact = false,
  onItemOpen,            // fallback handler: (item) => void
  sourceLabel,           // honesty line naming the REAL sources, e.g. 'activation · jobs · receipts' (useWorkspaceSignals passes the live one)
  emptyTitle = 'No signal yet',
  emptyBody = 'Nothing needs attention. New activity lands here as it happens.',
  footer,
  style: styleOverride,
}) {
  const [activeChannel, setActiveChannel] = useState(null)

  const channelMap = useMemo(() => {
    const map = new Map()
    channels.forEach((c) => map.set(c.key, c))
    return map
  }, [channels])

  const visible = useMemo(() => {
    const filtered = activeChannel ? items.filter((n) => n.channel === activeChannel) : items
    return filtered.slice(0, maxItems)
  }, [items, activeChannel, maxItems])

  const urgentCount = items.filter((n) => n.urgent).length
  const newCount = items.filter((n) => n.isNew).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, ...styleOverride }}>
      {/* Header — live dot, kicker, counters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            flexShrink: 0,
            background: live ? WORKSPACE_ACCENTS.green : 'rgba(255,255,255,0.16)',
            boxShadow: live ? `0 0 8px ${tint(WORKSPACE_ACCENTS.green, 55)}` : 'none',
            animation: live ? 'sc-signal-pulse 2s ease-in-out infinite' : 'none',
          }}
        />
        <span
          style={{
            fontSize: 9,
            letterSpacing: '0.26em',
            fontWeight: 700,
            textTransform: 'uppercase',
            color: WORKSPACE_TEXT.tertiary,
            fontFamily: WORKSPACE_FONT.mono,
          }}
        >
          {title}
        </span>
        {clock && (
          <span style={{ fontSize: 9, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.mono }}>
            {clock}
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {urgentCount > 0 && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 10,
                background: tint(WORKSPACE_ACCENTS.red, 14),
                border: `1px solid ${tint(WORKSPACE_ACCENTS.red, 28)}`,
                color: WORKSPACE_ACCENTS.red,
                fontFamily: WORKSPACE_FONT.mono,
              }}
            >
              {`${urgentCount} URGENT`}
            </span>
          )}
          {newCount > 0 && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 10,
                background: tint(WORKSPACE_ACCENTS.cyan, 12),
                border: `1px solid ${tint(WORKSPACE_ACCENTS.cyan, 24)}`,
                color: WORKSPACE_ACCENTS.cyan,
                fontFamily: WORKSPACE_FONT.mono,
              }}
            >
              {`${newCount} NEW`}
            </span>
          )}
        </span>
      </div>

      {/* Channel filter chips */}
      {filterable && channels.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[{ key: null, label: 'All', accent: WORKSPACE_TEXT.tertiary }, ...channels].map((c) => {
            const isActive = activeChannel === c.key
            const count = c.key ? items.filter((n) => n.channel === c.key).length : items.length
            return (
              <button
                key={c.key ?? '__all'}
                type="button"
                onClick={() => setActiveChannel(c.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 9px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 9.5,
                  fontWeight: isActive ? 700 : 500,
                  fontFamily: WORKSPACE_FONT.mono,
                  letterSpacing: '0.05em',
                  background: isActive ? tint(c.accent, 12) : WORKSPACE_SURFACE.card,
                  border: `1px solid ${isActive ? tint(c.accent, 26) : WORKSPACE_SURFACE.lineFaint}`,
                  color: isActive ? c.accent : WORKSPACE_TEXT.tertiary,
                }}
              >
                {c.label}
                <span style={{ opacity: 0.65 }}>{count}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Items / loading / honest empty */}
      {loading ? (
        <div
          style={{
            padding: '18px 0',
            fontSize: 10,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.14em',
          }}
        >
          LOADING SIGNAL…
        </div>
      ) : visible.length === 0 ? (
        <div
          style={{
            padding: '22px 14px',
            borderRadius: 10,
            border: `1px dashed ${WORKSPACE_SURFACE.line}`,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui }}>
            {emptyTitle}
          </div>
          <div style={{ marginTop: 4, fontSize: 11, lineHeight: 1.6, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.ui }}>
            {emptyBody}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {visible.map((item) => {
            const channel = channelMap.get(item.channel)
            return (
              <WorkspaceNotificationItem
                key={item.id}
                accent={item.accent || channel?.accent || WORKSPACE_ACCENTS.cyan}
                glyph={item.glyph ?? channel?.glyph}
                title={item.title}
                body={item.body}
                time={item.time}
                channelLabel={channel?.label}
                isNew={item.isNew}
                urgent={item.urgent}
                compact={compact}
                onOpen={item.onOpen || (onItemOpen ? () => onItemOpen(item) : undefined)}
              />
            )
          })}
        </div>
      )}

      {/* Source honesty line */}
      {sourceLabel && (
        <div
          style={{
            fontSize: 9,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.06em',
          }}
        >
          {sourceLabel}
        </div>
      )}
      {footer}
    </div>
  )
}
